import React, { useEffect } from 'react';
import { useStore } from '../../../../../../stores/root';
import { PPhPercentageOptions, PPNPercentageOptions } from '../../../../../../manifests/taxPercentage';

// assets

// services
import ValidationService from '../../../../../../services/validation';
import GeneralService from '../../../../../../services/general';

// props

// components
import { Checkbox, DefaultButton, DetailsList, Selection, IColumn, Panel, PanelType, PrimaryButton, SelectionMode, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { IIncomeInvoiceItemResourceShortProps } from '../../../../../../props/finance/incomes/invoices/item';
import Label from '../../../../../typography/label';
import Text from '../../../../../typography/text';
import { IIncomeInvoiceOrderItemResourceShortProps } from '../../../../../../props/finance/incomes/invoices/orderitem';
import { IOrderResourceProps } from '../../../../../../props/orders/order';
import OrderService from '../../../../../../services/orders/order';
import { IOrderGoodsResourceShortProps } from '../../../../../../props/orders/orderGoods';
import { IOrderTruckResourceShortProps } from '../../../../../../props/orders/trucks';
import { IOrderContainerResourceShortProps } from '../../../../../../props/orders/containers';
import Details from '../../../../../typography/details';
import { IGeneralPriceProps } from '../../../../../../props/general/price';
import { IOrderAddonResourceShortProps } from '../../../../../../props/orders/orderAddon';
import OrderTruckService from '../../../../../../services/orders/trucks';
import OrderContainerService from '../../../../../../services/orders/orderContainer';
import OrderGoodsService from '../../../../../../services/orders/orderGoods';
import OrderAddOnService from '../../../../../../services/orders/odrerAddOn';
import { IIncomeInvoiceResourceShortProps } from '../../../../../../props/finance/incomes/invoices/invoice';

type SelectIncomeInvoiceRelatedOrderItemFormProps = {
    orderId: string;
    existingInvoices: IIncomeInvoiceResourceShortProps[];
    orderItems?: IIncomeInvoiceOrderItemResourceShortProps[];
    onDismissed(orderItems?: IIncomeInvoiceOrderItemResourceShortProps[], priceDetails?: IGeneralPriceProps): void;
}

type RelatedItemProps = {
    goods?: IOrderGoodsResourceShortProps;
    truck?: IOrderTruckResourceShortProps;
    container?: IOrderContainerResourceShortProps;
    addon?: IOrderAddonResourceShortProps;
}

const SelectIncomeInvoiceRelatedOrderItemForm: React.FC<SelectIncomeInvoiceRelatedOrderItemFormProps> = (props: SelectIncomeInvoiceRelatedOrderItemFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [items, setItems] = React.useState<RelatedItemProps[]>([]);
    const [selectedItems, setSelectedItems] = React.useState<RelatedItemProps[]>([]);

    const _selection: Selection = new Selection({
        onSelectionChanged: () => {
            const selected = _selection.getSelection() as RelatedItemProps[];
            setSelectedItems(selected);
        }
    });

    const columns: IColumn[] = [
        {
            key: 'details',
            name: 'Details',
            minWidth: 100,
            onRender: (item: RelatedItemProps) => {
                return <Stack>
                    {item.container ? <>
                        <Label size={'small'}>Container {item.container.containerNumber || item.container.name}</Label>
                        {(item.container.sealNumber || "").trim() !== "" ? <Text size={'small'}>Seal number: {item.container.sealNumber}</Text> : null}
                    </> : null}
                    {item.goods ? <>
                        <Label size={'small'}>{item.goods.goods.name} ({item.goods.quantityAmount}{item.goods.goods.quantityUnit})</Label>
                    </> : null}
                    {item.truck ? <>
                        {item.truck.vehicleRegistrationNumber && !item.truck.truck ? <Label size={'small'}>Truck {item.truck.vehicleRegistrationNumber}</Label> : null}
                        {item.truck.truck && !item.truck.vehicleRegistrationNumber ? <Label size={'small'}>Truck {item.truck.truck.registrationNumber}</Label> : null}
                        {!item.truck.vehicleRegistrationNumber && !item.truck.truck ? <Label size={'small'}>Truck</Label> : null}
                    </> : null}
                    {item.addon ? <>
                        <Label size={'small'}>{item.addon.name}</Label>
                    </> : null}
                </Stack>;
            }
        },
        {
            key: 'price',
            name: 'Price',
            minWidth: 125,
            maxWidth: 125,
            onRender: (item: RelatedItemProps) => {
                const details = item.container || item.goods || item.truck || item.addon;

                return <Stack>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(details?.totalPrice || "0")}</Text>
                </Stack>
            }
        }
    ]

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const _order = await OrderService.get(props.orderId);
        const _items: RelatedItemProps[] = [];

        const [addons, containers, goods, trucks] = await Promise.all([
            OrderAddOnService.retrieve(props.orderId),
            OrderContainerService.retrieve(props.orderId),
            OrderGoodsService.retrieve.get(props.orderId),
            OrderTruckService.retrieve(props.orderId)
        ]);

        const existingAddons: IOrderAddonResourceShortProps[] = []
        const existingContainers: IOrderContainerResourceShortProps[] = [];
        const existingGoods: IOrderGoodsResourceShortProps[] = [];
        const existingTrucks: IOrderTruckResourceShortProps[] = [];

        props.existingInvoices.forEach((i) => {
            (i.orderItems || []).forEach((oi) => {
                if (oi.addon) {existingAddons.push(oi.addon);}
                else if (oi.container) {existingContainers.push(oi.container);}
                else if (oi.goods) {existingGoods.push(oi.goods);}
                else if (oi.truck) {existingTrucks.push(oi.truck);}
            })
        });

        (addons || []).map((addon) => {
            const eidx = existingAddons.findIndex((e) => e.id === addon.id);
            if (eidx < 0) {_items.push({ addon });}
        });
        (containers || []).map((container) => {
            const eidx = existingContainers.findIndex((e) => e.id === container.id);
            if (eidx < 0) {_items.push({ container });}
        });
        (goods || []).map((goods) => {
            const eidx = existingAddons.findIndex((e) => e.id === goods.id);
            if (eidx < 0) {_items.push({ goods });}
        });
        (trucks || []).map((truck) => {
            const eidx = existingTrucks.findIndex((e) => e.id === truck.id);
            if (eidx < 0) {_items.push({ truck });}
        });

        setItems(_items);
        setSelectedItems(props.orderItems || []);
        setLoaded(true);
    }

    const _onSubmit = async () => {
        try {
            const priceDetails = calculatePrice();
            props.onDismissed(selectedItems as IIncomeInvoiceOrderItemResourceShortProps[], priceDetails);
        } catch (e) {
        }
    }

    const calculatePrice = () => {
        let subtotalAmount = 0;
        let totalAmount = 0;
        let ppn = 0;
        let pph = 0;
        let invoiceAmount = 0;
        let ppnPercentage = "";
        let pphPercentage = "";

        selectedItems.forEach((item) => {
            const details = item.container || item.goods || item.truck || item.addon;

            if (details) {
                if (item.goods && item.goods.pricingMethod === 'perquantity') {
                    subtotalAmount += parseFloat(details.price + "") * parseFloat(item.goods.quantityAmount + "");
                } else {
                    subtotalAmount += parseFloat(details.price + "");
                }

                totalAmount += parseFloat(details.totalPrice + "");
                ppn += parseFloat(details.ppn + "");
                pph += parseFloat(details.pph + "");
                invoiceAmount += parseFloat(details.invoicePrice + "");

                if (ppnPercentage === "") {
                    ppnPercentage = details.ppnPercentage + "";
                } else if (ppnPercentage !== details.ppnPercentage + "") {
                    ppnPercentage = 'custom';
                }

                if (pphPercentage === "") {
                    pphPercentage = details.pphPercentage + "";
                } else if (pphPercentage !== details.pphPercentage + "") {
                    pphPercentage = 'custom';
                }
            }
        });

        return {
            subtotalAmount: subtotalAmount.toFixed(3),
            ppn: ppn.toFixed(3),
            pph: pph.toFixed(3),
            invoiceAmount: invoiceAmount.toFixed(3),
            totalAmount: totalAmount.toFixed(3),
            pphPercentage,
            ppnPercentage
        };
    }

    const renderPriceDetails = () => {
        const priceDetails = calculatePrice();

        return <Stack tokens={{ childrenGap: 15 }}>
            <Details title={'Total price (include PPn & PPh)'} text={`Rp. ${GeneralService.getNumberWithSeparator(priceDetails.totalAmount)}`} />
            <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Details title={'PPn'} text={`Rp. ${GeneralService.getNumberWithSeparator(priceDetails.ppn)}`} style={{ width: '50%' }} />
                <Details title={'PPh'} text={`Rp. ${GeneralService.getNumberWithSeparator(priceDetails.pph)}`} style={{ width: '50%' }} />
            </Stack>
            <Details title={'Invoice price'} text={`Rp. ${GeneralService.getNumberWithSeparator(priceDetails.invoiceAmount)}`} />
        </Stack>;
    }

    return <Panel headerText={'Select Related Order Items'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed()}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text={"Select"} onClick={_onSubmit} />
                <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed() }} />
            </Stack>;
        }}>

        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Retrieving items ..."} /></Stack> : null}
            {
                loaded && items.length > 0 ? <Stack tokens={{ childrenGap: 40 }}>
                    <DetailsList items={items}
                        columns={columns}
                        selection={_selection}
                        selectionMode={SelectionMode.multiple} />
                    {renderPriceDetails()}
                </Stack> : null
            }
            {loaded && items.length < 1 ? <Text size={'small'}>No order items exist. This can be because there are no items yet, or all order items have other invoice associated to.</Text> : null}
        </Stack>
    </Panel>
};

export default SelectIncomeInvoiceRelatedOrderItemForm;
